import { vaultFilesApi } from "@api";
import { queriesVaultFiles } from "@query";
import { resolveErrorMessage } from "@utils/resolveErrorMessage";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { downloadFileFromBuffer } from "../../utils/files";

/**
 * Copy for an attachment the app cannot retrieve at all. Deliberately states
 * no cause: a 404 on the vault read is indistinguishable between a legacy
 * pre-migration MemberFile id and a VaultFile that has since been deleted, so
 * any explanation we offered here would be a guess. Every surface listing
 * attachments shares this string so they cannot drift.
 */
export const BILLING_ATTACHMENT_UNAVAILABLE_LABEL = "Attachment unavailable";

/**
 * Copy for an attachment whose read failed for a reason that may not recur —
 * offline, a 5xx after retries, or a permission error. Kept separate from the
 * definitive case above because telling a user a file is gone when the request
 * merely failed is worse than admitting we could not load it.
 */
export const BILLING_ATTACHMENT_LOAD_FAILED_LABEL = "Couldn't load attachment";

type BillingAttachmentFile = {
  isLoading: boolean;
  /** True when the id definitively resolves to no readable file (a 404). */
  isUnavailable: boolean;
  /**
   * True when the read failed for a reason other than a 404, so the file may
   * well exist and be readable on a later attempt.
   */
  isLoadFailed: boolean;
  filename?: string;
  /** Undefined until the file resolves. Never rejects — see below. */
  download?: () => Promise<void>;
};

/**
 * Resolves an invoice/payable attachment's file for display and download.
 *
 * `BillingAttachment.memberFileId` spans two id spaces. Files attached since
 * the v3 migration are VaultFiles and read back fine. Anything attached before
 * it is a legacy public MemberFile, and such an id resolves nowhere the browser
 * can reach: every vault endpoint answers 404 for it ("MemberFile identifiers
 * are not VaultFile identifiers"), and the v1 files service that owns it is a
 * hard kill switch in this app — `WSService` throws `NotMigrated` instead of
 * making the request. So a pre-migration attachment reports as unavailable
 * rather than pretending to be downloadable; resolving it needs a v3 read that
 * accepts a MemberFile id (WIN-15660).
 *
 * `download` reports its own failures and settles rather than rejecting. Call
 * sites wire it straight to an `onClick`, where a rejected promise has nowhere
 * to go: it surfaced as an unhandled rejection in the dev overlay and as
 * silence in production. Owning the failure here means no caller can forget
 * it.
 */
export const useBillingAttachmentFile = (
  fileId: string
): BillingAttachmentFile => {
  const { openSnackbar } = useWSSnackbar();

  const queryVaultFile = queriesVaultFiles.getVaultFile.useQuery({
    fileId
  });

  const vaultFile = queryVaultFile.data;

  if (!vaultFile) {
    // Only a 404 means "there is no readable file behind this id". Treating
    // every failed read as unavailable asserted a permanent, specific cause
    // over what may be a transient network or server error.
    const isNotFound = queryVaultFile.error?.response?.status === 404;

    return {
      isLoading: queryVaultFile.isLoading,
      isUnavailable: isNotFound,
      isLoadFailed: !queryVaultFile.isLoading && !isNotFound
    };
  }

  const filename = vaultFile.filename ?? vaultFile.title ?? undefined;

  return {
    isLoading: false,
    isUnavailable: false,
    isLoadFailed: false,
    filename,
    // The content read is typed `void` by the SDK (the endpoint answers a
    // redirect), so the bytes come off the raw response rather than a typed
    // body — the same escape hatch the shared-file and paystub downloads use.
    download: async () => {
      try {
        const response = await vaultFilesApi.getVaultFileContentRaw({
          fileId
        });

        downloadFileFromBuffer(
          await response.raw.arrayBuffer(),
          filename ?? "download",
          vaultFile.mimeType
        );
      } catch (error) {
        openSnackbar({
          type: "warning",
          message: resolveErrorMessage({ error, context: "DownloadFile" })
        });
      }
    }
  };
};
